import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ImagePlus, Loader2, Trash2, Upload } from "lucide-react";
import { uploadAdminFile } from "../../redux/slices/fileSlice";
import { resolveAssetUrl } from "../../lib/asset-url";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useToast } from "../ui/use-toast";

/**
 * Admin-friendly image picker — uploads to the API and stores the returned URL.
 */
function ImageUploadField({
  label = "Image",
  hint,
  value = "",
  onChange,
  category = "content_media",
  disabled = false,
  className = "",
}) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewUrl = value ? resolveAssetUrl(value) : "";
  // Show preview for any stored upload URL (some CDN paths lack file extensions).
  const showPreview = Boolean(previewUrl);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please choose a JPG, PNG, GIF, or WebP image.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const result = await dispatch(
        uploadAdminFile({ file, category })
      ).unwrap();
      const url = result?.file_url;
      if (!url) throw new Error("Upload did not return a file URL");
      onChange(url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: typeof err === "string" ? err : "Could not upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    if (disabled) return;
    onChange("");
  };

  return (
    <div className={`grid gap-2 ${className}`}>
      <Label>{label}</Label>
      {hint ? (
        <p className="text-xs text-muted-foreground -mt-1">{hint}</p>
      ) : null}

      {showPreview ? (
        <div className="relative w-full max-w-xs rounded-lg border overflow-hidden bg-muted/30">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-40 object-cover"
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="destructive"
              className="h-8 w-8"
              disabled={disabled || uploading}
              onClick={clear}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 w-full max-w-xs h-36 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/30 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <ImagePlus className="h-8 w-8 text-muted-foreground" />
          )}
          <span className="text-sm text-muted-foreground">
            {uploading ? "Uploading…" : "Click to upload image"}
          </span>
          <span className="text-xs text-muted-foreground">
            JPG, PNG, GIF or WebP · max 5 MB
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}

export default ImageUploadField;
